import React, { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from './index';
import { Loader } from 'lucide-react';
import katex from 'katex';

/**
 * 매스컨셉 상세 정보 모달 공통 컴포넌트 (읽기 전용)
 *
 * @param {Object} props - 컴포넌트 props
 * @param {boolean} props.isOpen - 모달 열림/닫힘 상태
 * @param {function} props.onClose - 모달 닫기 핸들러
 * @param {function} props.renderLatex - LaTeX 렌더링 함수 (필수)
 * @param {function} [props.onTagClick] - 태그 클릭 핸들러 (선택)
 * @param {boolean} [props.showCloseButton=true] - 닫기 버튼 표시 여부
 * @param {boolean} [props.useLoader=false] - Loader 컴포넌트 사용 여부
 * @param {string} [props.title="매스컨셉 상세 정보"] - 모달 제목
 * @param {boolean} [props.handleAuth=true] - 401 에러 처리 여부
 * @param {boolean} [props.useBearerPrefix=false] - Bearer 접두사 사용 여부
 * @param {Array|number} [props.conceptIds] - 조회할 concept ID(s)
 * @param {Array} [props.data] - 직접 전달된 데이터 (API 호출 스킵)
 * @param {boolean} [props.loading] - 외부에서 관리하는 로딩 상태
 */
const MathceptDetailModal = ({
  isOpen,
  onClose,
  renderLatex: renderLatexProp,
  onTagClick,
  showCloseButton = true,
  useLoader = false,
  title = "매스컨셉 상세 정보",
  handleAuth = true,
  useBearerPrefix = false,
  conceptIds,
  data: externalData,
  loading: externalLoading
}) => {
  // renderLatex 기본 구현 (prop이 없을 경우 katex 사용)
  const defaultRenderLatex = useCallback((text) => {
    if (!text) return '';
    try {
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, { throwOnError: false, displayMode: false });
        } catch (e) { return match; }
      });
    } catch (error) { return text; }
  }, []);
  const renderLatex = renderLatexProp || defaultRenderLatex;

  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // 내부 상태 관리
  const [internalData, setInternalData] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // 실제 사용할 데이터와 로딩 상태
  const data = externalData !== undefined ? externalData : internalData;
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;

  // 상수 정의
  const conceptTypeOptions = [
    { value: 'DEF', label: '정의' },
    { value: 'AXM', label: '공리' },
    { value: 'IDN', label: '정리' },
    { value: 'THM', label: '이론' },
    { value: 'MTH', label: '방법론' },
    { value: 'PRP', label: '성질' },
    { value: 'FOR', label: '공식' },
    { value: 'NTN', label: '표기법' }
  ];

  const mathTypeOptions = [
    { value: 'AL', label: '대수학' },
    { value: 'AN', label: '해석학' },
    { value: 'GE', label: '기하학' },
    { value: 'PS', label: '확률과통계' }
  ];

  // 색상 함수
  const getConceptTypeColor = (type) => {
    const colors = {
      'DEF': 'rgba(239, 68, 68, 0.5)',
      'AXM': 'rgba(249, 115, 22, 0.5)',
      'IDN': 'rgba(245, 158, 11, 0.5)',
      'THM': 'rgba(34, 197, 94, 0.5)',
      'MTH': 'rgba(6, 182, 212, 0.5)',
      'PRP': 'rgba(59, 130, 246, 0.5)',
      'FOR': 'rgba(139, 92, 246, 0.5)',
      'NTN': 'rgba(236, 72, 153, 0.5)'
    };
    return colors[type] || 'rgba(156, 163, 175, 0.5)';
  };

  const getMathTypeColor = (type) => {
    const colors = {
      'AL': 'rgba(99, 102, 241, 0.5)',
      'AN': 'rgba(244, 63, 94, 0.5)',
      'GE': 'rgba(16, 185, 129, 0.5)',
      'PS': 'rgba(168, 85, 247, 0.5)',
    };
    return colors[type] || 'rgba(107, 114, 128, 0.5)';
  };

  // API 호출 함수
  const fetchMathceptDetails = useCallback(async (conceptIds) => {
    if (!conceptIds || (Array.isArray(conceptIds) && conceptIds.length === 0)) {
      return;
    }

    // 외부에서 데이터를 관리하는 경우 API 호출하지 않음
    if (externalData !== undefined) {
      return;
    }

    try {
      setInternalLoading(true);

      const token = await getAccessToken();
      if (!token && handleAuth) {
        navigate('/signin');
        return;
      }

      // concept_ids 파라미터 처리
      const idsParam = Array.isArray(conceptIds)
        ? conceptIds.join(',')
        : String(conceptIds);

      // Authorization 헤더 처리
      const authHeader = useBearerPrefix && !token.startsWith('Bearer ')
        ? `Bearer ${token}`
        : token;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mathcepts/temp/by-concept-ids?concept_ids=${idsParam}`,
        {
          method: 'GET',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
        }
      );

      // 401 에러 처리
      if (response.status === 401 && handleAuth) {
        navigate('/signin');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setInternalData(data.data || []);
      } else {
        console.error('Failed to fetch mathcept details:', response.status);
        alert('상세 정보를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Error fetching mathcept details:', error);
      alert('상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setInternalLoading(false);
    }
  }, [getAccessToken, navigate, handleAuth, useBearerPrefix, externalData]);

  // 모달이 열릴 때 API 호출
  React.useEffect(() => {
    if (isOpen && conceptIds && externalData === undefined) {
      fetchMathceptDetails(conceptIds);
    }
  }, [isOpen, conceptIds, fetchMathceptDetails, externalData]);

  // 태그 클릭 핸들러
  const handleTagClick = (tag) => {
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    if (externalData === undefined) {
      setInternalData([]);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="lg"
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          {useLoader ? (
            <Loader className="animate-spin" size={32} />
          ) : (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {data.map((item, index) => (
            <div
              key={item.concept_id}
              style={{
                marginBottom: index < data.length - 1 ? '24px' : '0',
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '1rem'
              }}
            >
              {/* 개념명 */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', flex: 1 }}>
                  <span dangerouslySetInnerHTML={{ __html: renderLatex(item.concept_name) }} />
                </h3>
              </div>

              <div style={{ marginBottom: '16px' }}>
                {/* 개념번호 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>개념번호:</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      backgroundColor: item.is_outdated ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)',
                      color: item.is_outdated ? '#b91c1c' : '#15803d',
                      borderRadius: '6px',
                      padding: '2px 10px',
                      fontSize: '13px',
                      minWidth: '36px',
                    }}
                  >
                    {item.order}
                  </span>
                </div>

                {/* 수학분야 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>수학분야:</span>
                  <span>{mathTypeOptions.find(opt => opt.value === item.math_type)?.label || item.math_type}</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      backgroundColor: getMathTypeColor(item.math_type),
                      borderRadius: '6px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      minWidth: '28px',
                    }}
                  >
                    {item.math_type}
                  </span>
                </div>

                {/* 개념유형 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>개념유형:</span>
                  <span>{conceptTypeOptions.find(opt => opt.value === item.concept_type)?.label || item.concept_type}</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      backgroundColor: getConceptTypeColor(item.concept_type),
                      borderRadius: '6px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      minWidth: '28px',
                    }}
                  >
                    {item.concept_type}
                  </span>
                </div>

                {/* 상태 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>상태:</span>
                  <span style={{ color: item.is_outdated ? '#ef4444' : '#4ABC85', fontWeight: 'bold' }}>
                    {item.is_outdated ? '구버전' : '활성'}
                  </span>
                </div>
              </div>

              {/* 설명 */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', flex: 1 }}>설명</h4>
                </div>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '1rem',
                    border: '1px solid #e5e7eb',
                    lineHeight: '1.6',
                    minHeight: item.descriptions ? 'auto' : '40px',
                    color: item.descriptions ? 'inherit' : '#9ca3af',
                    fontStyle: item.descriptions ? 'normal' : 'italic'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: item.descriptions ? renderLatex(item.descriptions) : '설명이 없습니다.'
                  }}
                />
              </div>

              {/* 태그 */}
              {item.tags && item.tags.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    태그
                  </h4>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {item.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '12px',
                          fontSize: '12px',
                          cursor: onTagClick ? 'pointer' : 'default',
                          transition: onTagClick ? 'all 0.2s' : 'none'
                        }}
                        onClick={onTagClick ? () => handleTagClick(tag) : undefined}
                        onMouseEnter={onTagClick ? (e) => {
                          e.target.style.backgroundColor = '#0ea5e9';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'scale(1.05)';
                        } : undefined}
                        onMouseLeave={onTagClick ? (e) => {
                          e.target.style.backgroundColor = '#e0f2fe';
                          e.target.style.color = '#0369a1';
                          e.target.style.transform = 'scale(1)';
                        } : undefined}
                        title={onTagClick ? `"${tag}" 태그로 검색` : undefined}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCloseButton && (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>
            닫기
          </Button>
        </div>
      )}
    </Modal>
  );
};

/**
 * 매스컨셉 상세 정보 관리를 위한 커스텀 훅
 *
 * @param {Object} options - 훅 옵션
 * @param {boolean} [options.handleAuth=true] - 401 에러 처리 여부
 * @param {boolean} [options.useBearerPrefix=false] - Bearer 접두사 사용 여부
 * @returns {Object} 상태와 함수들
 */
export const useMathceptDetail = (options = {}) => {
  const { handleAuth = true, useBearerPrefix = false } = options;
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchAndShow = useCallback(async (conceptIds) => {
    if (!conceptIds || (Array.isArray(conceptIds) && conceptIds.length === 0)) {
      return;
    }

    try {
      setLoading(true);

      const token = await getAccessToken();
      if (!token && handleAuth) {
        navigate('/signin');
        return;
      }

      // concept_ids 파라미터 처리
      const idsParam = Array.isArray(conceptIds)
        ? conceptIds.join(',')
        : String(conceptIds);

      // Authorization 헤더 처리
      const authHeader = useBearerPrefix && !token.startsWith('Bearer ')
        ? `Bearer ${token}`
        : token;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mathcepts/temp/by-concept-ids?concept_ids=${idsParam}`,
        {
          method: 'GET',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
        }
      );

      // 401 에러 처리
      if (response.status === 401 && handleAuth) {
        navigate('/signin');
        return;
      }

      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setIsModalOpen(true);
      } else {
        console.error('Failed to fetch mathcept details:', response.status);
        alert('상세 정보를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Error fetching mathcept details:', error);
      alert('상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, navigate, handleAuth, useBearerPrefix]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setData([]);
  }, []);

  return {
    isModalOpen,
    loading,
    data,
    fetchAndShow,
    closeModal
  };
};

export default MathceptDetailModal;
